import Dexie from 'dexie';

export const ProgressStatus = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed'
};

const db = new Dexie('BiblePWA');

// Current schema version
db.version(7).stores({
  chapterProgress: 'chapter_id, status, progress_percent, last_updated_at, dirty',
  books: 'id, name, chapters',
  sessions: '++id, startChapter, endChapter, last_listened_at, dirty',
  audioCache: 'url, data, timestamp'
}).upgrade(tx => {
  // Migration from completedChapters to chapterProgress
  return tx.completedChapters?.toArray().then(chapters => {
    if (!chapters) return; // Handle case where completedChapters doesn't exist
    
    const progressUpdates = chapters.map(chapter => ({
      chapter_id: chapter.chapter_id,
      status: chapter.is_completed ? ProgressStatus.COMPLETED : ProgressStatus.NOT_STARTED,
      progress_percent: chapter.is_completed ? 100 : 0,
      last_updated_at: chapter.last_updated_at || new Date().toISOString(),
      dirty: chapter.dirty || 1
    }));
    return tx.chapterProgress.bulkAdd(progressUpdates);
  });
});

export default db;

// Helper functions for chapter ID conversion
export const formatChapterId = (bookId, chapter) => `${bookId}:${chapter}`;
export const parseChapterId = (chapterId) => {
  const [bookId, chapter] = chapterId.split(':').map(Number);
  return { bookId, chapter };
};

export const initDB = async () => {
  const bookCount = await db.books.count();
  if (bookCount === 0) {
    const response = await fetch('/static/books.json');
    const books = await response.json();
    await db.books.bulkPut(books);
  }
};

// Audio cache functions
export const getCachedAudio = async (url) => {
  try {
    const cachedAudio = await db.audioCache.get(url);
    return cachedAudio?.data || null;
  } catch (error) {
    console.error('Error retrieving cached audio:', error);
    return null;
  }
};

export const cacheAudio = async (url, blob) => {
  try {
    await db.audioCache.put({
      url,
      data: blob,
      timestamp: new Date().getTime()
    });
    return true;
  } catch (error) {
    console.error('Error caching audio:', error);
    return false;
  }
};

// Cache management - used to clean old entries if needed
export const cleanOldCache = async (maxAgeHours = 24) => {
  const oldTimestamp = new Date().getTime() - (maxAgeHours * 60 * 60 * 1000);
  return db.audioCache.where('timestamp').below(oldTimestamp).delete();
};

export const getRecentListeningRanges = async () => {
  // Fetch data
  const [allProgress, books] = await Promise.all([
    db.chapterProgress.toArray(),
    db.books.toArray()
  ]);

  if (!allProgress.length) return [];

  const booksMap = books.reduce((acc, book) => {
    acc[book.id] = book;
    return acc;
  }, {});

  // Use status for started check as requested.
  const isStarted = (p) => p.status !== ProgressStatus.NOT_STARTED;
  const isComplete = (p) => p.status === ProgressStatus.COMPLETED;

  // Filter: Only include items that are at least Started.
  // Sort by Book/Chapter canonical order.
  const sortedItems = allProgress
    .filter(isStarted)
    .map(p => {
      const { bookId, chapter } = parseChapterId(p.chapter_id);
      return {
        ...p,
        bookId,
        chapter,
        bookName: booksMap[bookId]?.name || 'Unknown',
        bookTotalChapters: booksMap[bookId]?.chapters || 0
      };
    })
    .sort((a, b) => {
      if (a.bookId !== b.bookId) return a.bookId - b.bookId;
      return a.chapter - b.chapter;
    });

  if (!sortedItems.length) return [];

  // Grouping Logic
  const ranges = [];
  let currentRange = null;

  for (const item of sortedItems) {
    if (!currentRange) {
      currentRange = { chapters: [item], start: item, end: item };
      ranges.push(currentRange);
      continue;
    }

    const prevItem = currentRange.chapters[currentRange.chapters.length - 1];
    
    // Check adjacency
    let isAdjacent = false;
    if (item.bookId === prevItem.bookId) {
      if (item.chapter === prevItem.chapter + 1) isAdjacent = true;
    } else if (item.bookId === prevItem.bookId + 1) {
      if (prevItem.chapter === prevItem.bookTotalChapters && item.chapter === 1) isAdjacent = true;
    }

    // Merge rule: Merge only if adjacent AND previous is completed.
    if (isAdjacent && isComplete(prevItem)) {
      currentRange.chapters.push(item);
      currentRange.end = item;
    } else {
      currentRange = { chapters: [item], start: item, end: item };
      ranges.push(currentRange);
    }
  }

  // Format Output
  const getAudioUrl = (bookId, bookName, chapter) => {
    const paddedBookId = bookId.toString().padStart(2, '0');
    const paddedChapter = chapter.toString().padStart(3, '0');
    const unpaddedChapter = chapter.toString();
    return `/static/audio/esv/${paddedBookId}${paddedChapter}_${bookName} ${unpaddedChapter}.mp3`;
  };

  const processedRanges = ranges.map(range => {
    // Label Logic
    let label = '';
    const startBook = range.start.bookName;
    const endBook = range.end.bookName;
    
    if (startBook === endBook) {
      if (range.start.chapter === range.end.chapter) {
        label = `${startBook} ${range.start.chapter}`;
      } else {
        label = `${startBook} ${range.start.chapter}-${range.end.chapter}`;
      }
    } else {
      label = `${startBook} ${range.start.chapter} - ${endBook} ${range.end.chapter}`;
    }

    // Last Updated (max)
    const lastUpdated = range.chapters.reduce((max, ch) => {
      const time = ch.last_updated_at || '';
      return (time > max) ? time : max;
    }, '');

    // Resume Logic
    // If the LAST item in the range is Completed, resume from the NEXT chapter.
    // Otherwise resume from the last item itself (which is incomplete).
    const lastItem = range.end;
    let resumeTarget = lastItem;
    
    if (isComplete(lastItem)) {
      // Find next chapter
      let nextBookId = lastItem.bookId;
      let nextChapterNum = lastItem.chapter + 1;
      let nextBookName = lastItem.bookName;
      
      if (nextChapterNum > lastItem.bookTotalChapters) {
         // Next book
         nextBookId++;
         const nextBook = booksMap[nextBookId];
         if (nextBook) {
           nextChapterNum = 1;
           nextBookName = nextBook.name;
           
           resumeTarget = {
             bookId: nextBookId,
             bookName: nextBookName,
             chapter: nextChapterNum
           };
         }
         // If no next book (end of Bible), stay on last item or handle as done? 
         // For now, default to last item if we can't find next.
      } else {
        resumeTarget = {
           bookId: nextBookId,
           bookName: nextBookName,
           chapter: nextChapterNum
         };
      }
    }

    const formattedResume = {
      bookId: resumeTarget.bookId,
      book: resumeTarget.bookName,
      chapter: resumeTarget.chapter,
      url: getAudioUrl(resumeTarget.bookId, resumeTarget.bookName, resumeTarget.chapter)
    };

    return {
      label,
      lastUpdated,
      formattedResume
    };
  });

  // Sort by lastUpdated desc and take top 5
  return processedRanges
    .sort((a, b) => {
        const dateA = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
        const dateB = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
        return dateB - dateA;
    })
    .slice(0, 5);
};
